import { Component, computed, EventEmitter, inject, Input, OnInit, Output } from "@angular/core";
import { FormBuilder, FormControl, ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { AttachmentCardComponent } from "@components/attachment-card/attachment-card.component";
import { ConfigurationWizardHeaderAccordionComponent } from "@components/configuration-wizard-header-accordion/configuration-wizard-header-accordion.component";
import { ConfigurationWizardHeaderComponent } from "@components/configuration-wizard-header/configuration-wizard-header.component";
import { FilterProductComponent } from "@components/filter-product/filter-product.component";
import { RefreshProductsHintComponent } from "@components/refresh-products-hint/refresh-products-hint.component";
import { Gas } from "@domain/gas/gas";
import { FGDS_REGARD_3000_PLUS_MATERIAL_TYPE_MAP } from "@domain/product/fgds-product-type-resolver";
import {
  FgdsProductType,
  FullProductType,
  isRequiredAttachment,
  LocalizeProductNamePipe,
  MatchType,
  Product,
  Relationship,
  RelationshipSubType,
  RelationshipType,
} from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { untilDestroyed } from "@odx/angular/utils";
import { first, forkJoin, map, Observable, of } from "rxjs";

export interface AttachmentSelectorProducts {
  selectedProducts: Product[];
  preselectedAttachments?: { product: Product; quantity: number }[];
}

@Component({
  selector: "app-attachment-selector",
  templateUrl: "./attachment-selector.component.html",
  styleUrls: ["./attachment-selector.component.scss"],
  imports: [
    UiKitModule,
    ReactiveFormsModule,
    AttachmentCardComponent,
    ConfigurationWizardHeaderComponent,
    ConfigurationWizardHeaderAccordionComponent,
    FilterProductComponent,
    RefreshProductsHintComponent,
  ],
  providers: [LocalizeProductNamePipe],
  standalone: true,
})
export class AttachmentSelectorComponent implements OnInit {
  private productService = inject(ProductService);
  private formBuilder = inject(FormBuilder);
  private localizeProductNamePipe = inject(LocalizeProductNamePipe);

  @Input({ required: true }) set products(products: AttachmentSelectorProducts) {
    this.selectedProducts = products.selectedProducts as Product[];
    this.collectAttachments();
    this.updateHeaderTitle();
    this.clearSelection();

    if (products.preselectedAttachments) {
      this.preselectedAttachments = products.preselectedAttachments;
      this.selectPreselectedAttachments(products.preselectedAttachments);
      return;
    }
    this.selectRequiredAttachments();
  }

  @Input() showCosts = false;
  @Input() selectionMode: "single" | "multi" = "multi";
  @Input() headerProducts: (Product | Gas)[] = [];
  @Input()
  set excludedFullTypes(value: FullProductType[] | undefined) {
    this._excludedFullTypes = value ?? [];
    if (this.selectedProducts.length) {
      this.collectAttachments();
    }
  }

  @Output() attachmentsSelect$ = new EventEmitter<{ product: Product; quantity: number }[]>();

  protected radioButtonForm = this.formBuilder.group({
    selectedAttachment: new FormControl<Product | null>(null),
  });

  protected isSingleProductHeaderShown = computed(() => {
    return this.headerProducts.length === 1;
  });

  @Output()
  formReady = of(this.radioButtonForm);

  protected selectedProducts: Product[] = [];
  protected filteredReqAttachments$!: Observable<Product[]>;
  protected filteredOptAttachments$!: Observable<Product[]>;
  protected headerTitle: string = "";
  protected headerAccordionPrefix: string = $localize`:@@productConfiguration.attachments.headerAccordionPrefix:Zubehör für`;

  private optAttachments$!: Observable<Product[]>;
  private reqAttachments$!: Observable<Product[]>;
  private selectedAttachments: { product: Product; quantity: number }[] = [];
  private preselectedAttachments: { product: Product; quantity: number }[] = [];
  private _excludedFullTypes: FullProductType[] = [];
  private takeUntilDestroyed = untilDestroyed();

  ngOnInit(): void {
    this.radioButtonForm.valueChanges.pipe(this.takeUntilDestroyed()).subscribe((formValue) => {
      if (!formValue?.selectedAttachment) {
        return;
      }
      const selectedAttachments = [{ product: formValue.selectedAttachment, quantity: 1 }];
      this.selectedAttachments = selectedAttachments;
      this.attachmentsSelect$.emit(selectedAttachments);
    });
  }

  protected search(value: string) {
    this.filteredReqAttachments$ = this.productService.filter(this.reqAttachments$, value);
    this.filteredOptAttachments$ = this.productService.filter(this.optAttachments$, value);
  }

  protected updateAttachmentSelection(quantity: number, attachment: Product) {
    if (this.selectionMode === "single") {
      this.updateSingleSelectedAttachment(quantity, attachment);
    } else {
      this.updateMultiSelectedAttachments(quantity, attachment);
    }
  }

  protected getSelectedQuantity(product: Product) {
    const selectedAttachment = this.selectedAttachments.find((attachment) => attachment.product.id === product.id);
    return selectedAttachment?.quantity ?? 0;
  }

  protected getPreselectedQuantity(product: Product) {
    const selectedAttachment = this.preselectedAttachments.find((attachment) => attachment.product.id === product.id);
    return selectedAttachment?.quantity ?? 0;
  }

  private selectPreselectedAttachments(preselectedAttachments: { product: Product; quantity: number }[]) {
    forkJoin([this.reqAttachments$, this.optAttachments$])
      .pipe(
        first(),
        map(([required, optional]) => [...required, ...optional]),
        map((available) => this.filterValidPreselectedAttachments(preselectedAttachments, available)),
      )
      .subscribe((attachments) =>
        attachments.forEach((attachment) => this.updateAttachmentSelection(attachment.quantity, attachment.product)),
      );
  }

  private selectRequiredAttachments() {
    this.reqAttachments$
      .pipe(first())
      .subscribe((attachments) => attachments.forEach((attachment) => this.updateAttachmentSelection(1, attachment)));
  }

  private filterValidPreselectedAttachments(
    preselectedAttachments: { product: Product; quantity: number }[],
    available: Product[],
  ) {
    return preselectedAttachments
      .filter(
        (attachment) =>
          attachment.product.isAvailable && attachment.product.id && available.some((a) => a.id === attachment.product.id),
      )
      .map((attachment) => ({
        product: available.find((a) => a.id === attachment.product.id)!,
        quantity: attachment.quantity,
      }));
  }

  private clearSelection() {
    if (this.selectionMode === "single") {
      this.radioButtonForm.patchValue({ selectedAttachment: null });
    } else {
      this.selectedAttachments = [];
      this.attachmentsSelect$.emit(this.selectedAttachments);
    }
  }

  private updateSingleSelectedAttachment(quantity: number, attachment: Product) {
    if (quantity === 0) {
      this.radioButtonForm.patchValue({ selectedAttachment: null }, { emitEvent: false });
      this.selectedAttachments = [];
      this.attachmentsSelect$.emit([]);
      return;
    }
    this.radioButtonForm.patchValue({ selectedAttachment: attachment }, { emitEvent: false });
    this.selectedAttachments = [{ product: attachment, quantity }];
    this.attachmentsSelect$.emit([{ product: attachment, quantity }]);
  }

  protected updateMultiSelectedAttachments(quantity: number, attachment: Product) {
    const updatedSelectedAttachments = this.selectedAttachments.filter(
      (selectedAttachment) => selectedAttachment.product.id !== attachment.id,
    );
    if (quantity > 0) {
      updatedSelectedAttachments.push({ product: attachment, quantity });
    }
    this.selectedAttachments = updatedSelectedAttachments;
    this.attachmentsSelect$.emit(this.selectedAttachments);
  }

  private collectAttachments() {
    const reqIds = [
      ...new Set(
        this.selectedProducts
          .flatMap((product) => product.relationships)
          .filter(
            (relationship) =>
              isRequiredAttachment(relationship) &&
              relationship.relationshipSubType !== RelationshipSubType.GATEWAY &&
              relationship.relationshipSubType !== RelationshipSubType.INPUT &&
              relationship.relationshipSubType !== RelationshipSubType.RELAY &&
              relationship.relationshipSubType !== RelationshipSubType.SLOT_COVER,
          )
          .map((attachment) => attachment.productId),
      ),
    ];

    const optIds = [
      ...new Set(
        this.selectedProducts
          .flatMap((product) => product.relationships)
          .filter(
            (relationship) =>
              this.isOptionalAttachment(relationship) &&
              relationship.relationshipSubType !== RelationshipSubType.GATEWAY &&
              relationship.relationshipSubType !== RelationshipSubType.INPUT &&
              relationship.relationshipSubType !== RelationshipSubType.RELAY &&
              relationship.relationshipSubType !== RelationshipSubType.SLOT_COVER,
          )
          .map((attachment) => attachment.productId),
      ),
    ];

    this.reqAttachments$ = this.getAttachments(reqIds);
    this.filteredReqAttachments$ = this.reqAttachments$;
    this.optAttachments$ = this.getAttachments(optIds);
    this.filteredOptAttachments$ = this.optAttachments$;
  }

  private getAttachments(ids: string[]) {
    return ids.length
      ? this.productService
          .getProductsByIds(ids, FgdsProductType.ATTACHMENT)
          .pipe(
            map((products) =>
              products.filter(
                (product) => product.isAvailable && (!this._excludedFullTypes.length || !this.isExcludedProduct(product)),
              ),
            ),
          )
      : of([]);
  }

  private isExcludedProduct(product: Product): boolean {
    const legacyFullType = FGDS_REGARD_3000_PLUS_MATERIAL_TYPE_MAP.get(product.id);
    return (
      product.fullType === undefined ||
      this._excludedFullTypes.includes(product.fullType) ||
      (legacyFullType !== undefined && this._excludedFullTypes.some((fullType) => fullType === legacyFullType))
    );
  }

  private isOptionalAttachment(relationship: Relationship): boolean {
    const isMatchTypeDirect = relationship.matchType == MatchType.DIRECT;
    const isOptional = relationship.relationshipType == RelationshipType.EXTRA;
    const isModule = relationship.relationshipType == RelationshipType.MODULE;
    return isMatchTypeDirect && (isOptional || isModule);
  }

  private updateHeaderTitle() {
    if (this.selectedProducts.length === 1) {
      this.headerTitle = this.localizeProductNamePipe.transform(this.selectedProducts[0]);
    } else if (this.selectedProducts.length > 1) {
      if (this.selectedProducts.some((p) => p.type === FgdsProductType.ALARMDEVICE)) {
        this.headerTitle = $localize`:@@alarmDeviceConfiguration.summaryHeader:${this.selectedProducts.length}:numberOfSelectedProducts: ausgewählte Alarmmittel`;
        return;
      }
      if (this.selectedProducts.some((p) => p.type === FgdsProductType.GASWARNINGCENTER)) {
        this.headerTitle = $localize`:@@gasWarningCenterConfiguration.summaryHeader:${this.selectedProducts.length}:numberOfSelectedProducts: ausgewählte Produkte`;
        return;
      }
      this.headerTitle = $localize`:@@generalConfiguration.summaryHeader:${this.selectedProducts.length}:numberOfSelectedProducts: ausgewählte Produkte`;
    }
  }
}
